import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';

// KeyboardLiftFooterDirective
// ---------------------------
// Purpose: Keep an <ion-footer> visually pinned just above the software
// keyboard across iOS versions and webview behaviors.
//
// Context:
// - With Capacitor Keyboard (resize: 'ionic'), Ionic often reduces the
//   available space by adding body padding-bottom and/or shrinking the
//   layout viewport.
// - On newer iOS (e.g., iOS 26), visualViewport may or may not shrink when
//   the keyboard appears; on older iOS (e.g., iOS 18), keyboardHeight is
//   sometimes the most reliable signal.
//
// Strategy:
// 1) Compute the visible bottom of the page using the most trustworthy of
//    these candidates (prefer direct measurements; avoid double subtraction):
//    - visualViewport.bottom (vv.height + vv.offsetTop)
//    - window.innerHeight - bodyPaddingBottom (Ionic's keyboard padding)
//    - window.innerHeight - keyboardHeight (fallback when others don't apply)
// 2) Take the minimum of the available candidates to get the actual visible
//    bottom. Then lift the footer only by the required overlap + a gap.
// 3) Clamp lift so it never exceeds keyboardHeight + gap to prevent overshoot.
// 4) Listen to visualViewport resize/scroll and body style/class mutations to
//    catch late adjustments when navigating or when iOS finalizes its layout.
//
// Result: Works on iOS 18 and 26, and avoids the "double height" or "late
// jump" issues when the keyboard appears or when navigating back to a page.
@Directive({
  selector: '[smKeyboardLiftFooter]'
})
export class KeyboardLiftFooterDirective implements OnInit, OnDestroy {
  @Input('smKeyboardLiftFooter') gap: number | string = 18;

  private didShowHandle: any;
  private didHideHandle: any;
  private lastKeyboardHeight = 0;
  private vvResizeHandler: any;
  private vvScrollHandler: any;
  private rafId: number | null = null;
  private bodyObserver: MutationObserver | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone
  ) {}

  ngOnInit() {
    // Hint for smoother transform-based movement of the footer.
    // Hint for smoother movement
    this.renderer.setStyle(this.el.nativeElement, 'will-change', 'transform');
    this.renderer.setStyle(this.el.nativeElement, 'transition', 'transform 60ms linear');

    // Always watch visualViewport changes; we'll decide whether to lift based on open-state heuristics
    this.attachVisualViewportListeners();
    this.attachBodyObserver();
    try {
      // We only use keyboardDidShow to capture keyboardHeight for fallback and
      // for clamping. The actual positioning is driven by updateLift() which
      // relies on viewport metrics to avoid double-counting.
      this.didShowHandle = Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
        this.zone.runOutsideAngular(() => this.onShow(info));
      });
      this.didHideHandle = Keyboard.addListener('keyboardDidHide', () => {
        this.zone.runOutsideAngular(() => this.onHide());
      });
    } catch (_) {
      // noop
    }
  }

  ngOnDestroy() {
    try {
      if (this.didShowHandle && this.didShowHandle.remove) {
        this.didShowHandle.remove();
      }
      if (this.didHideHandle && this.didHideHandle.remove) {
        this.didHideHandle.remove();
      }
      this.detachVisualViewportListeners();
      this.detachBodyObserver();
      this.renderer.setStyle(this.el.nativeElement, 'transform', '');
    } catch (_) {}
  }

  private onShow(info: KeyboardInfo) {
    try {
      // Cache for fallback/clamp. Some iOS versions do not shrink the
      // viewport, so keyboardHeight is the only reliable signal there.
      this.lastKeyboardHeight = info?.keyboardHeight || 0;
      this.updateLift();
    } catch (_) {}
  }

  private updateLift() {
    try {
      const rect = this.el.nativeElement.getBoundingClientRect();
      // Resolve desired gap (handle empty attribute case where Angular passes "")
      const defaultGap = 18;
      let gapVal: number = defaultGap;
      if (typeof this.gap === 'number' && isFinite(Number(this.gap))) {
        gapVal = Number(this.gap);
      } else if (typeof this.gap === 'string') {
        const t = (this.gap as string).trim();
        const parsed = t ? parseInt(t, 10) : NaN;
        gapVal = isNaN(parsed) ? defaultGap : parsed;
      }
      // Sources of truth for visible bottom; avoid double subtracting.
      const vv: any = (window as any).visualViewport;
      const vvBottom = (vv && typeof vv.height === 'number') ? (vv.height + (vv.offsetTop || 0)) : NaN;
      // Ionic keyboard resize adds body padding-bottom – treat this as a direct
      // reduction of the visible area.
      const bodyPad = parseFloat((window.getComputedStyle(document.body).paddingBottom || '0').replace('px', '')) || 0;
      // If visualViewport is shorter than layout viewport, the keyboard is
      // occluding content even if keyboardHeight is not available.
      const vvShorter = (vv && typeof vv.height === 'number') ? (vvBottom < (window.innerHeight - 1)) : false;

      const candidates: number[] = [];
      if (!isNaN(vvBottom)) {
        candidates.push(vvBottom);
      }
      if (bodyPad > 0) {
        candidates.push(window.innerHeight - bodyPad);
      }
      if (this.lastKeyboardHeight > 0 && !vvShorter && bodyPad === 0) {
        candidates.push(window.innerHeight - this.lastKeyboardHeight);
      }
      if (candidates.length === 0) {
        candidates.push(window.innerHeight);
      }
      const visibleBottom = Math.min.apply(Math, candidates);

      // Consider the keyboard "open" if any signal is present. This prevents
      // applying a stale transform when navigating back to a page.
      const isKeyboardOpen = this.lastKeyboardHeight > 0 || vvShorter || bodyPad > 8;
      if (!isKeyboardOpen) {
        this.renderer.setStyle(this.el.nativeElement, 'transform', '');
        return;
      }

      const overlap = (rect.bottom + gapVal) - visibleBottom;
      let lift = Math.max(0, overlap);
      // Clamp lift to avoid overshooting due to transient metrics.
      if (this.lastKeyboardHeight > 0) {
        const maxLift = this.lastKeyboardHeight + gapVal;
        lift = Math.min(lift, maxLift);
      }
      this.renderer.setStyle(this.el.nativeElement, 'transform', lift ? `translate3d(0, -${lift}px, 0)` : '');
    } catch (_) {}
  }

  private onHide() {
    this.lastKeyboardHeight = 0;
    this.renderer.setStyle(this.el.nativeElement, 'transform', '');
    this.detachVisualViewportListeners();
  }

  private attachVisualViewportListeners() {
    try {
      const vv: any = (window as any).visualViewport;
      if (!vv) {
        return;
      }
      if (!this.vvResizeHandler) {
        // Debounce with requestAnimationFrame so we update after the browser
        // finishes the resize/scroll step, avoiding flicker and double work.
        this.vvResizeHandler = () => {
          if (this.rafId) {
            cancelAnimationFrame(this.rafId);
          }
          this.rafId = requestAnimationFrame(() => this.updateLift());
        };
        vv.addEventListener('resize', this.vvResizeHandler);
      }
      if (!this.vvScrollHandler) {
        this.vvScrollHandler = () => {
          if (this.rafId) {
            cancelAnimationFrame(this.rafId);
          }
          this.rafId = requestAnimationFrame(() => this.updateLift());
        };
        vv.addEventListener('scroll', this.vvScrollHandler);
      }
    } catch (_) {}
  }

  private attachBodyObserver() {
    try {
      if (this.bodyObserver) {
        return;
      }
      // Ionic toggles body padding/class names when handling keyboard resize.
      // Observing attributes lets us recompute when those changes land,
      // including when returning to a page with the keyboard already open.
      this.bodyObserver = new MutationObserver(() => {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }
        this.rafId = requestAnimationFrame(() => this.updateLift());
      });
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    } catch (_) {}
  }

  private detachBodyObserver() {
    try {
      if (this.bodyObserver) {
        this.bodyObserver.disconnect();
        this.bodyObserver = null;
      }
    } catch (_) {}
  }

  private detachVisualViewportListeners() {
    try {
      const vv: any = (window as any).visualViewport;
      if (!vv) {
        return;
      }
      if (this.vvResizeHandler) {
        vv.removeEventListener('resize', this.vvResizeHandler);
        this.vvResizeHandler = null;
      }
      if (this.vvScrollHandler) {
        vv.removeEventListener('scroll', this.vvScrollHandler);
        this.vvScrollHandler = null;
      }
    } catch (_) {}
  }
}


